import React, { useMemo } from 'react';
import { Avatar, Typography, Space, Spin } from 'antd';
import { UserOutlined, RobotOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { marked } from 'marked';
import type { ChatMessage as ChatMessageType } from '@/services/chat';

const { Text, Paragraph } = Typography;

export interface ChatMessageProps {
  message: ChatMessageType;
  showAvatar?: boolean;
  showTimestamp?: boolean;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  showAvatar = true,
  showTimestamp = true,
}) => {
  const { content, isUser, timestamp, isStreaming } = message;

  const messageStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: isUser ? 'row-reverse' : 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 16,
    justifyContent: isUser ? 'flex-end' : 'flex-start',
  };

  const bubbleStyle: React.CSSProperties = {
    maxWidth: '70%',
    padding: '12px 16px',
    borderRadius: 16,
    position: 'relative',
    backgroundColor: isUser ? '#1890ff' : '#f5f5f5',
    color: isUser ? '#fff' : '#262626',
    wordBreak: 'break-word',
    lineHeight: 1.5,
  };

  const avatarColor = isUser ? '#1890ff' : '#52c41a';

  // Configure marked options for better security and styling
  const markedOptions = useMemo(() => {
    marked.setOptions({
      breaks: true, // Convert '\n' to <br>
      gfm: true, // Enable GitHub Flavored Markdown
    });
    return marked;
  }, []);

  const formatMessageContent = (text: string): React.ReactNode => {
    if (isUser) {
      // For user messages, keep simple text formatting
      return text.split('\n').map((line, index, array) => (
        <React.Fragment key={index}>
          {line}
          {index < array.length - 1 && <br />}
        </React.Fragment>
      ));
    } else {
      // For agent messages, render markdown
      // Note: marked does not sanitize HTML by default. 
      // For production, consider adding DOMPurify for additional security.
      const htmlContent = markedOptions.parse(text);
      return (
        <div 
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          style={{
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              margin: '8px 0 4px 0',
              color: 'inherit',
            },
            '& p': {
              margin: '4px 0',
            },
            '& ul, & ol': {
              margin: '4px 0',
              paddingLeft: '20px',
            },
            '& li': {
              margin: '2px 0',
            },
            '& code': {
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              padding: '2px 4px',
              borderRadius: '3px',
              fontSize: '13px',
              fontFamily: 'SFMono-Regular, Consolas, Liberation Mono, Menlo, monospace',
            },
            '& pre': {
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              padding: '8px',
              borderRadius: '6px',
              overflow: 'auto',
              margin: '8px 0',
            },
            '& pre code': {
              backgroundColor: 'transparent',
              padding: 0,
            },
            '& blockquote': {
              borderLeft: '3px solid #ddd',
              paddingLeft: '12px',
              margin: '8px 0',
              fontStyle: 'italic',
            },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              margin: '8px 0',
            },
            '& th, & td': {
              border: '1px solid #ddd',
              padding: '6px 8px',
              textAlign: 'left',
            },
            '& th': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              fontWeight: 'bold',
            },
          } as React.CSSProperties}
        />
      );
    }
  };

  return (
    <div style={messageStyle}>
      {showAvatar && (
        <Avatar
          size={32}
          style={{ 
            backgroundColor: avatarColor,
            flexShrink: 0,
          }}
          icon={isUser ? <UserOutlined /> : <RobotOutlined />}
        />
      )}
      
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
        <div style={bubbleStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1 }}>
              {content ? (
                isUser ? (
                  <Text 
                    style={{ 
                      color: '#fff',
                      margin: 0,
                      fontSize: 14,
                    }}
                  >
                    {formatMessageContent(content)}
                  </Text>
                ) : (
                  <div
                    style={{
                      color: '#262626',
                      fontSize: 14,
                      lineHeight: 1.5,
                    }}
                  >
                    {formatMessageContent(content)}
                  </div>
                )
              ) : (
                <Text 
                  style={{ 
                    color: isUser ? '#fff' : '#8c8c8c',
                    fontStyle: 'italic',
                    fontSize: 14,
                  }}
                >
                  {isStreaming ? 'Thinking...' : 'No content'}
                </Text>
              )}
            </div>
            
            {isStreaming && !isUser && (
              <Spin 
                size="small" 
                style={{ 
                  color: '#8c8c8c',
                  marginLeft: 4,
                }}
              />
            )}
          </div>
        </div>
        
        {showTimestamp && (
          <Text 
            type="secondary" 
            style={{ 
              fontSize: 11,
              marginTop: 4,
              textAlign: isUser ? 'right' : 'left',
            }}
          >
            {dayjs(timestamp).format('HH:mm')}
          </Text>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
